import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { AdminHealthOverview } from '@activepieces/shared';

import { HEALTH_POLL_INTERVAL_MS } from '../health/constants';
import { raisedHealthAlerts } from '../health/health-alerts';

import { HEALTH_ROUTE } from './constants';

const describeStatus = ({
  isError,
  isHealthy,
  alerts,
}: {
  isError: boolean;
  isHealthy: boolean;
  alerts: string[];
}): string => {
  if (isError) {
    return t('Platform health could not be loaded');
  }
  if (isHealthy) {
    return t('All systems operational');
  }
  return t('Needs attention: {alerts}', { alerts: alerts.join(', ') });
};

// Same query key as the Health page: both read one cached answer, and
// switching pages does not refetch it.
export const HealthStatusLine = () => {
  const { data: health, isError } = useQuery<AdminHealthOverview, Error>({
    queryKey: ['admin-dashboard-health'],
    queryFn: () => adminApi.getDashboardHealth(),
    refetchInterval: HEALTH_POLL_INTERVAL_MS,
  });

  if (!health && !isError) {
    return <Skeleton className="h-12 w-full" />;
  }

  const alerts = health ? raisedHealthAlerts(health) : [];
  const isHealthy = health !== undefined && alerts.length === 0;

  return (
    <Link to={HEALTH_ROUTE}>
      <Card
        className={
          isHealthy
            ? 'flex items-center gap-2.5 px-4 py-3 hover:bg-muted/40 transition-colors'
            : 'flex items-center gap-2.5 px-4 py-3 border-destructive/30 bg-destructive/5 hover:bg-destructive/10 transition-colors'
        }
      >
        {isHealthy ? (
          <CheckCircle2 className="size-4 text-success dark:text-green-400 shrink-0" />
        ) : (
          <AlertTriangle className="size-4 text-destructive shrink-0" />
        )}
        <span
          className={
            isHealthy ? 'text-sm' : 'text-sm font-medium text-destructive'
          }
        >
          {describeStatus({ isError, isHealthy, alerts })}
        </span>
        <ChevronRight className="size-4 text-muted-foreground ml-auto shrink-0" />
      </Card>
    </Link>
  );
};
